import classNames from 'classnames';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import { type FC, useState, useRef, useEffect } from 'react';
import { generateFullName } from 'utils/badgeHelpers';
import useUser from 'utils/useUser';
import TrashDeleteIcon from 'components/atoms/icons/trashIcon';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { Button, Flex } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

import s from './styles.module.scss';

import type { IActivityComments } from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { TDeleteCommentFn } from 'infrastructure/hooks/rpm';

interface MessageItemProps {
  isMockComment: boolean;
  comment: IActivityComments;
  isErroneousComment?: boolean;
  deleteComment?: TDeleteCommentFn;
}

const MessageItem: FC<MessageItemProps> = ({
  isMockComment,
  comment,
  isErroneousComment,
  deleteComment,
}) => {
  const { t } = useTranslation();
  const { userType, isAdmin } = useUser();
  const { Alert, showAlert } = useAlert();
  const [expanded, setExpanded] = useState(false);
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const contentRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const element = contentRef.current;
      element.classList.add(s.truncated);
      const needsTruncation = element.scrollHeight > element.clientHeight + 1;
      element.classList.remove(s.truncated);
      setShouldTruncate(needsTruncation);
    }
  }, [comment.message]);

  const handleDelete = async () => {
    const message = `${t('labels.commentDeleteConfirm')} ${t(
      'labels.commentDeleteConfirmInteractiveTime',
    )}`;
    const alertAnswer = await showAlert({
      title: t('labels.commentDeleteTitle'),
      messageTitle: message,
      successBtnLabel: t('controls.yes'),
      rejectBtnLabel: t('controls.no'),
    });

    if (alertAnswer) {
      await deleteComment?.({
        guid: comment.guid,
        userType,
      });
    }
  };

  return (
    <li
      data-cy="comment"
      className={classNames(s['comment-item'], {
        [s.mock]: isMockComment,
      })}
      key={comment.guid}
      data-new-action={comment?.newAction}
    >
      {isAdmin && (
        <div className={s['top-right-actions']}>
          <button
            className={s['delete-button']}
            onClick={handleDelete}
            aria-label="Delete comment"
          >
            <TrashDeleteIcon />
          </button>
        </div>
      )}
      {Alert}
      <p
        ref={contentRef}
        data-cy="comment-text"
        className={classNames(s['comment-message'], {
          [s.truncated]: !expanded && shouldTruncate,
        })}
      >
        {isErroneousComment && (
          <span style={{ display: 'block', lineHeight: 1 }}>
            <b>{t('overviewDashboard.reason')}:</b>
          </span>
        )}
        {comment.message}
      </p>
      {shouldTruncate && (
        <Button
          type="link"
          onClick={() => setExpanded(!expanded)}
          className={s['expand-button']}
        >
          <Flex gap={8} className={s['expand-button']}>
            {expanded ? (
              <>
                {t('labels.showLess', 'Show less')} <UpOutlined />
              </>
            ) : (
              <>
                {t('labels.showMore', 'Show more')} <DownOutlined />
              </>
            )}
          </Flex>
        </Button>
      )}
      <div data-cy="signed-by" className={s['comment-signed-by']}>
        <p>
          <span>{t('labels.electronicallySignedBy')}: </span>
          <span>
            {generateFullName(
              comment.createdByTitle,
              comment.createdByFirstName,
              '',
              comment.createdByLastName,
              comment.createdByProfessionType,
            )}
          </span>
          <span> • </span>
          <span>
            {DateFormatter({
              date: comment.createdAt,
              format: DateFormats['MM/dd/yy, hh:mm a'],
            })}
          </span>
        </p>
      </div>
    </li>
  );
};
export default MessageItem;
